import React, { forwardRef, memo, useMemo, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  AvatarGroup,
  Box,
  Center,
  Icon,
  Flex,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaAlignLeft } from "react-icons/fa";
import { format as formatDate, parseISO as parseISODate } from "date-fns";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import {
  PUBLISHED,
  ERROR_STATUSES,
  SCHEDULED_STATUSES,
} from "../../../_constants/PostStatus";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import SocialAccountPropType from "../../../_types/SocialAccount";
import PostPropType from "../../../_types/Post";
import PostAdditionalPropType from "../../../_types/PostAdditional";
import { getSocialProviderBySocialType } from "../../../_utils/socialProvider";
import PostMenu from "../PostMenu/PostMenu";
import DraftMenu from "../PostMenu/DraftMenu";
import ApproverInfo from "../ApproverInfo/ApproverInfo";
import PostStatusIcon from "../PostStatusIcon/PostStatusIcon";
import DraftStatusIcon from "../DraftStatusIcon/DraftStatusIcon";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import SimplePostCover from "./SimplePostCover";
import SimplePostLink from "./SimplePostLink";
import MultipliedPostDetailsButton from "../PostMultiply/MultipliedPostDetailsButton";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";

const horizontalMediaSize = "56px";

const postStyles = {
  position: "relative",
};

const postErrorStyles = {
  ...postStyles,
  boxShadow: "0 0 0 3px var(--chakra-colors-brand-negative)",
  borderRadius: "2px",
};

const postSelectedStyles = {
  ...postStyles,
  boxShadow: "0 0 0 3px var(--chakra-colors-brand-primary)",
  borderRadius: "2px",
};

const layoutHorizontalStyles = (isPast, hasError, isSelected) => ({
  ...(hasError ? postErrorStyles : []),
  ...(isSelected ? postSelectedStyles : []),
  backgroundColor: isPast ? "#232323" : "brand.grey-darker",
  color: isPast ? "gray.200" : "gray.100",
  borderRadius: "md",
  marginBottom: 2,
  display: "flex",
});

const imagePlaceholderStyles = {
  borderRadius: "md",
  height: horizontalMediaSize,
  width: horizontalMediaSize,
  background: "gray.400",
};

const socialIconsWrapStyles = {
  position: "absolute",
  bottom: "1",
  left: "1",
};

const headingStyles = (isPast) => ({
  color: isPast ? "gray.200" : "gray.100",
  width: "full",
  paddingTop: 2,
  paddingRight: 2,
  paddingLeft: 3,
  paddingBottom: 2,
  position: "relative",
});

const contentStyles = (isPast) => ({
  color: isPast ? "gray.200" : "gray.100",
  width: "full",
  paddingBottom: 2,
  paddingRight: 2,
  paddingLeft: 2,
  paddingTop: 2,
  zIndex: 2,
});

const menuButtonStyles = (isPast) => ({
  h: "5",
  w: "6",
  minW: "6",
  fontSize: "xs",
  color: isPast ? "gray.200" : "gray.100",
  mt: "-0.5",
});

const mediaStyles = {
  height: "9em",
  width: "full",
  position: "absolute",
  top: 0,
  left: 0,
  _after: {
    borderRadius: "lg",
    content: '""',
    position: "absolute",
    pointerEvents: "none",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 30%, transparent 56%, rgba(0,0,0,0.8) 96%)",
  },
};

const mediaHorizontalStyles = {
  height: horizontalMediaSize,
  width: horizontalMediaSize,
};

const contentLineHeight = 1.2;

const SimplePost = forwardRef(
  (
    {
      accounts,
      additional,
      draft,
      compressed,
      isPast,
      isSelected,
      onDelete,
      onClick,
      onEdit,
      onOpen,
      onMultiply,
      onMultipliedPostDetails,
      onPublishNow,
      onReuse,
      onUse,
      onShare,
      showMenu,
      post,
      type,
      layout,
      customStatusIcon,
      customMenu,
      customEmptyDateTimeText,
      hasError,
      styles,
    },
    ref
  ) => {
    const { t } = useTranslation("planner");
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const account = useMemo(() => {
      const accountId = post.accounts[0];
      return accounts.find((item) => item.id === accountId);
    }, [accounts, post.accounts]);
    const carousel = useMemo(() => {
      const postAdditional = post?.additional || [];
      const provider = getSocialProviderBySocialType(account?.social_type);

      return postAdditional.find(
        (item) =>
          item.source === provider && item.type === PostAdditionalTypes.CAROUSEL
      );
    }, [post, account]);
    const mediaItems = useMemo(
      () => getFixedPhotosVideosOrder(post?.photos, post?.videos).items,
      [post?.photos, post?.videos]
    );

    const date = useMemo(
      () =>
        post.date && post.time
          ? parseISODate(`${post.date} ${post.time}`)
          : null,
      [post.date, post.time]
    );
    const dateTitle = useMemo(
      () =>
        date
          ? `${formatDate(date, "EEE, LLL do, yyyy, p", dateFnsOptions())} ${
              post?.timezone ? `(${post.timezone?.replaceAll("_", " ")})` : ""
            }`
          : "",
      [date, post?.timezone]
    );

    const isDraft = useMemo(() => type === "draft", [type]);
    const isPost = useMemo(() => type === "post", [type]);
    const isCustom = useMemo(() => type === "custom", [type]);
    const isCompressed = isDraft || isSmallScreen || compressed;

    const isHorizontal = useMemo(() => layout === "horizontal", [layout]);

    const hasSuccessStatus = useMemo(
      () => isPost && PUBLISHED === additional?.status,
      [isPost, additional?.status]
    );

    const hasErrorStatus = useMemo(
      () => hasError || (isPost && ERROR_STATUSES.includes(additional?.status)),
      [isPost, additional?.status, hasError]
    );

    const hasScheduledStatus = useMemo(
      () => isPost && SCHEDULED_STATUSES.includes(additional?.status),
      [isPost, additional?.status]
    );

    const isMultipliedPost = useMemo(() => isPost && !!additional?.multiply_parent, [isPost, additional?.multiply_parent]);

    const renderMenu = (menuProps) => {
      if (customMenu) {
        return typeof customMenu === "function"
          ? customMenu({ ...menuProps })
          : customMenu;
      } else if (isPost) {
        return (
          <PostMenu
            {...menuProps}
            onDelete={hasScheduledStatus ? onDelete : undefined}
            onEdit={hasScheduledStatus ? onEdit : undefined}
            onMultiply={onMultiply}
            onOpen={hasSuccessStatus ? onOpen : undefined}
            onPublishNow={hasScheduledStatus ? onPublishNow : undefined}
            onReuse={onReuse}
            onShare={hasSuccessStatus ? onShare : undefined}
            socialUrl={additional?.socialUrl}
          />
        );
      } else if (isDraft) {
        return (
          <DraftMenu
            {...menuProps}
            variant="ghost"
            onDelete={onDelete}
            onEdit={onEdit}
            onUse={onUse}
            draft={draft}
          />
        );
      }

      return null;
    };

    const firstMedia = mediaItems?.[0];
    const hasLink = post.scraper?.link;
    const showLink = !firstMedia && !carousel && hasLink;
    const showCarousel = !firstMedia && !!carousel;
    const hasMedia = firstMedia || showLink || showCarousel;

    useImperativeHandle(ref, () => ({
      get hasMedia() {
        return hasMedia;
      },
    }));

    if (isHorizontal) {
      return (
        <Flex
          sx={{ ...layoutHorizontalStyles(isPast, hasErrorStatus, isSelected), ...styles }}
          cursor={onClick ? "pointer" : null}
          onClick={onClick}
        >
          <Box
            h={horizontalMediaSize}
            mr={isCompressed ? "2" : "4"}
            position="relative"
          >
            {hasMedia ? (
              <Box sx={mediaHorizontalStyles}>
                {firstMedia ? (
                  <SimplePostCover
                    src={firstMedia.data.thumbUrl}
                    isVideo={firstMedia.field === "videos"}
                    isPast={isPast}
                  />
                ) : null}
                {showLink ? (
                  <SimplePostLink
                    link={post.scraper.link}
                    image={post.scraper.image}
                    isPast={isPast}
                    urlShown={false}
                  />
                ) : null}
                {showCarousel ? (
                  <SimplePostCover
                    src={carousel.items?.[0]?.image}
                    isPast={isPast}
                  />
                ) : null}
              </Box>
            ) : (
              <Center sx={imagePlaceholderStyles}>
                <Icon as={FaAlignLeft} />
              </Center>
            )}
            <Box sx={socialIconsWrapStyles}>
              <SocialAccountAvatar
                name={account?.name}
                src={account?.image}
                socialType={account?.social_type}
                socialTypeShown
                size="xs"
                opacity={isPast ? "0.5" : "1"}
              />
            </Box>
          </Box>
          <Flex
            flex="1 1 0%"
            sx={
              isCompressed
                ? {
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-around",
                  }
                : {
                    marginRight: "4",
                  }
            }
          >
            <Flex alignItems="center">
              <Flex
                alignItems="flex-start"
                justifyContent="center"
                flexDirection="column"
                flex="1"
              >
                <Flex alignItems="center" minWidth={isCompressed ? null : "11.5rem"}>
                  {isPost ? (
                    <PostStatusIcon status={additional?.status} />
                  ) : null}
                  {isDraft ? (
                    <DraftStatusIcon draft={draft} />
                  ) : null}
                  {isCustom && customStatusIcon ? customStatusIcon : null}
                  <Text
                    fontSize="xs"
                    fontWeight="500"
                    lineHeight="1em"
                    whiteSpace="nowrap"
                    mr="2"
                    ml="2"
                    title={
                      dateTitle ||
                      customEmptyDateTimeText ||
                      t("simple_post.date_time.not_set")
                    }
                  >
                    {date
                      ? formatDate(date, "p", dateFnsOptions())
                      : customEmptyDateTimeText ||
                        t("simple_post.date_time.not_set")}
                  </Text>
                  <Text
                    fontSize="xs"
                    lineHeight="1em"
                    title={
                      dateTitle ||
                      customEmptyDateTimeText ||
                      t("simple_post.date_time.not_set")
                    }
                  >
                    {date
                      ? formatDate(date, "dd.MM.yyyy", dateFnsOptions())
                      : ""}
                  </Text>
                  {isMultipliedPost && onMultipliedPostDetails ? (
                    <MultipliedPostDetailsButton
                      postId={post.id}
                      masterId={additional.multiply_parent}
                      buttonProps={{
                        marginLeft: "2",
                        marginTop: "-2px",
                      }}
                      onClick={onMultipliedPostDetails}
                    />
                  ) : null}
                </Flex>
                <Flex alignItems="center" flex="1">
                  {additional?.approvedByUser && (
                    <ApproverInfo
                      approvedBy={additional.approvedByUser}
                      mt="1"
                    />
                  )}
                  {additional?.rejectedByUser && (
                    <ApproverInfo
                      rejectedBy={additional.rejectedByUser}
                      mt="1"
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Flex flex={isCompressed ? null : "1"} alignItems="center">
              <Text
                fontSize="xs"
                noOfLines={isCompressed ? 1 : 3}
                flex="1 1 0%"
                lineHeight={`${contentLineHeight}em`}
                wordBreak="break-all"
              >
                {post.message}
              </Text>
            </Flex>
          </Flex>
          {showMenu && (
          <Flex px={isCompressed ? "1" : "2"} alignItems="center">
            {renderMenu({
              buttonProps: isCompressed
                ? menuButtonStyles(isPast)
                : {
                    h: "8",
                    w: "8",
                    minW: "8",
                  },
            })}
          </Flex>
          )}
        </Flex>
      );
    }

    return (
      <Box
        sx={
          isSelected
            ? postSelectedStyles
            : hasErrorStatus
            ? postErrorStyles
            : postStyles
        }
        cursor={onClick ? "pointer" : null}
        onClick={onClick}
      >
        <Box minH={hasMedia ? "9em" : "5em"}>
          {hasMedia ? (
            <Box sx={mediaStyles}>
              {firstMedia ? (
                <SimplePostCover
                  src={firstMedia.data.thumbUrl}
                  isVideo={firstMedia.field === "videos"}
                  isPast={isPast}
                />
              ) : null}
              {showLink ? (
                <SimplePostLink
                  link={post.scraper.link}
                  image={post.scraper.image}
                  isPast={isPast}
                />
              ) : null}
              {showCarousel ? (
                <SimplePostCover
                  src={carousel.items?.[0]?.image}
                  isPast={isPast}
                />
              ) : null}
            </Box>
          ) : null}
          <Flex
            sx={headingStyles(isPast)}
            alignItems="center"
            mb={hasMedia ? "4em" : 0}
          >
            {isPost ? <PostStatusIcon status={additional?.status} /> : null}
            {isDraft ? <DraftStatusIcon draft={draft} /> : null}
            <Flex justifyContent="space-between" flex="1">
              <Flex
                alignItems="center"
                ml="2"
                flex="1"
                title={
                  dateTitle ||
                  customEmptyDateTimeText ||
                  t("simple_post.date_time.not_set")
                }
              >
                <Text
                  fontSize="xs"
                  fontWeight="500"
                  lineHeight="1em"
                  whiteSpace="nowrap"
                >
                  {date
                    ? formatDate(date, "p", dateFnsOptions())
                    : customEmptyDateTimeText ||
                      t("simple_post.date_time.not_set")}
                </Text>
                {isMultipliedPost && onMultipliedPostDetails ? (
                  <MultipliedPostDetailsButton
                    postId={post.id}
                    masterId={additional.multiply_parent}
                    buttonProps={{
                      marginLeft: "2",
                      marginTop: "-2px",
                    }}
                    onClick={onMultipliedPostDetails}
                  />
                ) : null}
              </Flex>
              {showMenu && (
              <Flex>
                {renderMenu({
                  buttonProps: menuButtonStyles(isPast),
                })}
              </Flex>
              )}
            </Flex>
          </Flex>
          <Box sx={contentStyles(isPast)}>
            <Flex alignItems="center" position="relative" zIndex="0">
              <AvatarGroup marginRight="2">
                <SocialAccountAvatar
                  name={account?.name}
                  src={account?.image}
                  socialType={account?.social_type}
                  socialTypeShown
                  size="xs"
                  opacity={isPast ? "0.5" : "1"}
                />
              </AvatarGroup>
              <Text
                fontSize="xs"
                noOfLines="2"
                flex="1 1 0%"
                lineHeight={`${contentLineHeight}em`}
                wordBreak="break-all"
              >
                {post.message}
              </Text>
            </Flex>
          </Box>
        </Box>
      </Box>
    );
  }
);

SimplePost.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  additional: PostAdditionalPropType.isRequired,
  compressed: PropTypes.bool,
  isPast: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onOpen: PropTypes.func,
  onMultiply: PropTypes.func,
  onMultipliedPostDetails: PropTypes.func,
  onPublishNow: PropTypes.func,
  onReuse: PropTypes.func,
  onUse: PropTypes.func,
  onShare: PropTypes.func,
  showMenu: PropTypes.bool,
  post: PostPropType.isRequired,
  layout: PropTypes.oneOf(["horizontal"]),
  type: PropTypes.oneOf(["post", "draft", "custom"]),
  customStatusIcon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  customMenu: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  customEmptyDateTimeText: PropTypes.string,
  hasError: PropTypes.bool,
};

SimplePost.defaultProps = {
  compressed: false,
  isPast: false,
  isSelected: false,
  onClick: undefined,
  onDelete: undefined,
  onEdit: undefined,
  onOpen: undefined,
  onMultiply: undefined,
  onMultipliedPostDetails: undefined,
  onPublishNow: undefined,
  onReuse: undefined,
  onUse: undefined,
  onShare: undefined,
  showMenu: true,
  layout: undefined,
  type: "post",
  customStatusIcon: undefined,
  customMenu: undefined,
  customEmptyDateTimeText: undefined,
  hasError: undefined,
};

export default memo(SimplePost);
